/**
 * TenantManagement — TASK-022 / flow-04, flow-22
 * Actor: sys_admin only
 * Route: /admin/tenants
 * API: GET/POST /api/v1/tenants
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppModal,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type Tenant = {
  id: number;
  name: string;
  code: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  prospect_project_id: string | null;
  is_active: boolean;
  created_at: string | null;
};

function CreateTenantModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    prospect_api_token: "",
    prospect_project_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.code) { setErr("Name and Code are required"); return; }
    setBusy(true); setErr("");
    try {
      const payload: Record<string, string> = {
        name: form.name,
        code: form.code,
      };
      if (form.contact_name) payload.contact_name = form.contact_name;
      if (form.contact_email) payload.contact_email = form.contact_email;
      if (form.contact_phone) payload.contact_phone = form.contact_phone;
      if (form.address) payload.address = form.address;
      if (form.prospect_api_token) payload.prospect_api_token = form.prospect_api_token;
      if (form.prospect_project_id) payload.prospect_project_id = form.prospect_project_id;
      await api.post("/tenants", payload);
      onSuccess(); onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed");
    }
    finally { setBusy(false); }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Create Tenant">
      <form onSubmit={submit}>
        <AppField label="Organisation Name" error={err}>
          <AppInput id="tenant-name" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} autoFocus />
        </AppField>
        <AppField label="Short Code (e.g. GRN)">
          <AppInput id="tenant-code" type="text" value={form.code} onChange={(e) => set("code", e.target.value)} disabled={busy} />
        </AppField>
        <AppField label="Contact Name">
          <AppInput id="tenant-contact-name" type="text" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Contact Email">
            <AppInput id="tenant-email" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Contact Phone">
            <AppInput id="tenant-phone" type="tel" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Address">
          <AppInput id="tenant-address" type="text" value={form.address} onChange={(e) => set("address", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Prospect Project ID">
            <AppInput id="tenant-project-id" type="text" value={form.prospect_project_id} onChange={(e) => set("prospect_project_id", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Prospect API Token">
            <AppInput id="tenant-prospect-token" type="password" value={form.prospect_api_token} onChange={(e) => set("prospect_api_token", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14}/> Creating…</> : "Create Tenant"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function TenantManagement() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setPageState("loading");
    try {
      const d = await api.get<{ tenants: Tenant[] }>("/tenants");
      setTenants(d.tenants ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load tenants");
      setPageState("error");
    }
  };

  useEffect(() => { load(); }, []);

  const columns: ColumnDef<Tenant, unknown>[] = [
    { accessorKey: "name", header: "Organisation" },
    { accessorKey: "code", header: "Code" },
    { id: "contact", header: "Contact", accessorFn: (r) => r.contact_name ?? "—" },
    { accessorKey: "contact_email", header: "Email" },
    { accessorKey: "contact_phone", header: "Phone" },
    { id: "project", header: "Prospect Project", accessorFn: (r) => r.prospect_project_id ?? "—" },
    {
      id: "status", header: "Status",
      cell: ({ row }) =>
        row.original.is_active
          ? <AppBadge tone="success"><IconCheck size={12}/> Active</AppBadge>
          : <AppBadge tone="neutral">Inactive</AppBadge>,
    },
    {
      id: "created_at",
      header: "Created",
      accessorFn: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—",
    },
  ];

  const listStatus =
    pageState === "success" && tenants.length === 0 ? "empty" : pageState;

  return (
    <>
      {createOpen && <CreateTenantModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={load} />}
      <PageHeader
        title="Tenants"
        subtitle="Organisations on the platform."
        actions={<AppButton type="button" onClick={() => setCreateOpen(true)}>New Tenant</AppButton>}
      />
      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No tenants"
        emptyHint="Create a tenant to onboard an organisation."
      >
        <AppCard><AppDataTable data={tenants} columns={columns} /></AppCard>
      </PageState>
    </>
  );
}
